// src/components/ModalProProfileEdit.js
import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchProfessionalById } from '../api/queries';
import { AuthContext } from '../context/AuthContext';
import { ModalContext } from '../context/ModalContext';
import { ModalHeaderContext } from '../context/ModalHeaderContext';
import { TabContext } from '../context/TabContext';
import API from '../api/client';
import ModalImageHeader from './ModalImageHeader';
import './ModalProProfileEdit.css';

export default function ModalProProfileEdit() {
  const { user } = useContext(AuthContext);
  const professionalId = user?.professional_id;
  const queryClient = useQueryClient();

  const { closeModal, openModal, modalStacks } = useContext(ModalContext);
  const { activeTab } = useContext(TabContext);
  const { registerHeaderActions } = useContext(ModalHeaderContext);

  const currentStack = modalStacks[activeTab] || [];
  const proModal = currentStack
    .slice()
    .reverse()
    .find(m => m.metadata?.modalType === 'proProfileEdit');
  const modalId = proModal?.id;

  // States for file, preview URL, and crop settings
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  // Fetch professional data
  const { data: profData, isLoading, isError } = useQuery({
    queryKey: ['professional', professionalId],
    queryFn: () => fetchProfessionalById(professionalId),
    enabled: !!professionalId
  });

  // Fetch blob URL of header for accurate natural dimensions
  const { data: blobUrl } = useQuery({
    queryKey: ['profImage', profData?.cabecera],
    queryFn: async () => {
      const res = await fetch(profData.cabecera);
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    },
    enabled: !!profData?.cabecera,
    staleTime: 24 * 60 * 60_000,
    cacheTime: 24 * 60 * 60_000
  });

  // Initialize preview and crop from server data
  useEffect(() => {
    if (!profData) return;
    const initialUrl = blobUrl || profData.cabecera;
    setPreviewUrl(initialUrl);
    if (profData.cabecera_crop) {
      const { crop: c, zoom: z, croppedAreaPixels: cap } = profData.cabecera_crop;
      setCrop(c);
      setZoom(z);
      setCroppedAreaPixels(cap);
    } else {
      // reset
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    }
  }, [profData, blobUrl]);

  // Refs & states for CSS cropping
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [imgStyle, setImgStyle] = useState({
    position: 'absolute',
    width: '100%',
    height: '100%',
    left: '0',
    top: '0',
    objectFit: 'cover'
  });

  // On image load, capture natural size
  const handleImgLoad = () => {
    if (!imgRef.current) return;
    setNaturalSize({
      width: imgRef.current.naturalWidth,
      height: imgRef.current.naturalHeight
    });
  };

  // Calculate cropping CSS whenever container, crop or naturalSize changes
  useEffect(() => {
    const cropRect = croppedAreaPixels || { x: 0, y: 0, width: naturalSize.width, height: naturalSize.height };
    const cont = containerRef.current;
    if (!cont || !naturalSize.width) {
      setImgStyle({
        position: 'absolute',
        width: '100%',
        height: '100%',
        left: '0',
        top: '0',
        objectFit: 'cover'
      });
      return;
    }
    const contW = cont.clientWidth;
    const contH = (contW * 2) / 5;
    const scale = contW / cropRect.width;
    const scaledW = naturalSize.width * scale;
    const scaledH = naturalSize.height * scale;

    const left = scaledW < contW ? (contW - scaledW) / 2 : -cropRect.x * scale;
    const top = scaledH < contH ? (contH - scaledH) / 2 : -cropRect.y * scale;

    setImgStyle({
      position: 'absolute',
      width: `${scaledW}px`,
      height: `${scaledH}px`,
      left: `${left}px`,
      top: `${top}px`,
      objectFit: 'cover'
    });
  }, [naturalSize, croppedAreaPixels]);

  // Persist crop or upload new file
  const persistCrop = useCallback(async () => {
    if (!croppedAreaPixels) return;
    if (selectedFile) {
      const { data: presign } = await API.post(
        '/profesionales/header-upload-url/',
        { filename: selectedFile.name, content_type: selectedFile.type }
      );
      const formData = new FormData();
      Object.entries(presign.fields).forEach(([k, v]) => formData.append(k, v));
      formData.append('file', selectedFile);
      const res = await fetch(presign.url, { method: 'POST', body: formData });
      if (!res.ok) throw new Error('Upload failed');
      await API.patch(`/profesionales/${professionalId}/`, {
        cabecera: presign.public_url,
        cabecera_crop: { crop, zoom, croppedAreaPixels }
      });
    } else {
      await API.patch(`/profesionales/${professionalId}/`, {
        cabecera_crop: { crop, zoom, croppedAreaPixels }
      });
    }
    queryClient.invalidateQueries(['professional', professionalId]);
  }, [selectedFile, croppedAreaPixels, crop, zoom, professionalId, queryClient]);

  // Register Save/Cancel
  useEffect(() => {
    if (!modalId) return;
    registerHeaderActions(modalId, {
      onSave: async () => {
        try {
          await persistCrop();
        } catch (err) {
          console.error(err);
          setErrorMsg('Error al guardar.');
          return;
        }
        closeModal(activeTab);
      },
      onCancel: () => {
        setSelectedFile(null);
        setErrorMsg('');
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        setPreviewUrl(blobUrl || profData?.cabecera || '');
        closeModal(activeTab);
      }
    });
  }, [modalId, persistCrop, registerHeaderActions, closeModal, activeTab, profData, blobUrl]);

  // Open crop modal
  const handleOpenImageModal = () => {
    openModal(
      <ModalImageHeader
        initialImageUrl={previewUrl || profData?.cabecera || ''}
        initialCrop={crop}
        initialZoom={zoom}
        initialCroppedAreaPixels={croppedAreaPixels}
        onCancel={() => closeModal(activeTab)}
        onConfirm={({ file, croppedArea, zoom: z, crop: c }) => {
          if (file) {
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
          }
          setCrop(c);
          setZoom(z);
          setCroppedAreaPixels(croppedArea);
          closeModal(activeTab);
        }}
      />,
      { tab: activeTab,
        overlayClassName: 'pet-detail-overlay',
      }
    );
  };

  if (isLoading) return <div className="modalProfOverlay"><p>Cargando...</p></div>;
  if (isError || !profData) return <div className="modalProfOverlay"><p>Error al cargar datos.</p></div>;

  return (
    <div className="modalProfOverlay">
      <div className="modalProfCard">
        <div
          className="modalProProfilefHeaderImageEdit clickable"
          ref={containerRef}
          onClick={handleOpenImageModal}
          style={{ cursor: 'pointer' }}
        >
          <img
            ref={imgRef}
            src={previewUrl || profData.cabecera}
            alt="Cabecera profesional"
            onLoad={handleImgLoad}
            style={imgStyle}
          />
        </div>

        {/* Divisor */}
        <div className="modalProfHeaderDivider" />

        <div className="modalProEditForm">
          {/* otros campos… */}
        </div>
        {errorMsg && <p className="error-msg">{errorMsg}</p>}
      </div>
    </div>
  );
}
